import { all, select, takeEvery, fork, call, put, delay } from 'redux-saga/effects'

// action
import { adminUsersActions, configActions, routeActions } from '@redux/store/index'
import { AdminUsersStateType } from '@redux/index'

// api
import { usersApi, ResType } from '@api'

// lib
import { Logger } from '@utils'

// type
import { User, UserType } from '@schemas'

const apiGetUsers = async (
	includingDeletedUsersYn: string,
): Promise<ResType | undefined> => {
	const res: ResType | undefined = await usersApi.getUsers({
		query: {
			includingDeletedUsersYn: includingDeletedUsersYn,
		},
	})
	return res
}

const apiBatchUpdateUsers = async (
	body: {
		iudFlag: string
		id: string
		name: string
		email: string
		language: string
		useYn: number
	}[],
): Promise<ResType | undefined> => {
	const res: ResType | undefined = await usersApi.batchUpdateUsers({
		body: { dataList: body },
	})
	return res
}

const apiUpdateUserPassword = async ({
	id,
	name,
	password,
}: {
	id: string
	name: string
	password: string
}) => {
	const res: ResType | undefined = await usersApi.updateUserPassword({
		params: { id: id },
		body: {
			name: name,
			password: password,
		},
	})
	return res
}

const apiUpdateUserProfileImage = async (userId: string, files: File[]) => {
	const res: ResType | undefined = await usersApi.updateUserProfileImage({
		params: {
			id: userId,
		},
		files: files,
	})
	return res
}

function* triggerInitAdminUsers() {
	try {
		yield put(adminUsersActions.USER_LIST_STATUS('loading'))
		const apiRes: ResType = yield call(apiGetUsers, '0')

		if (apiRes.status === 200) {
			const tempUserList =
				apiRes.data[0].users !== undefined ? (apiRes.data[0].users as any[]) : []
			const newUserlist = tempUserList.map(aUser => new User(aUser as UserType))
			yield put(adminUsersActions.USER_LIST(newUserlist))

			if (apiRes.data.length > 0) {
				yield put(adminUsersActions.USER_LIST_STATUS('ok'))
			} else {
				yield put(adminUsersActions.USER_LIST_STATUS('empty'))
			}
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* updateResetPassword() {
	try {
		const updateInfo: AdminUsersStateType['updateResetPassword'] = yield select(
			state => state.adminUsers.updateResetPassword,
		)

		const apiRes: ResType = yield call(apiUpdateUserPassword, updateInfo)

		if (apiRes !== undefined && apiRes.status === 201) {
			yield put(
				configActions.TOAST({ isShow: true, message: '패스워드가 업데이트 되었습니다.' }),
			)
			delay(2000)
			yield put(routeActions.NAVIGATE_PATH('/admin/users'))
		} else {
			yield put(
				adminUsersActions.RESET_PASSWORD_ERROR_MESSAGE(
					'패스워드 업데이트에 실패하였습니다.',
				),
			)
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* editUserProfileImage() {
	try {
		yield put(routeActions.NAVIGATE_PATH('/admin/user-profile-image-update'))
	} catch (error) {
		Logger.warn(error)
	}
}

function* updateProfileImage() {
	try {
		const profileImageInfo: AdminUsersStateType['upldateProfileImage'] = yield select(
			state => state.adminUsers.upldateProfileImage,
		)

		const apiRes: ResType = yield call(
			apiUpdateUserProfileImage,
			profileImageInfo.userId,
			[profileImageInfo.profileImage] as File[],
		)
	} catch (error) {
		Logger.warn(error)
	}
}

function* updateUser() {
	try {
		const body: AdminUsersStateType['updateUser'] = yield select(
			state => state.adminUsers.updateUser,
		)

		yield call(apiBatchUpdateUsers, [body])
	} catch (error) {
		Logger.warn(error)
	}
}

function* mainSaga() {
	yield takeEvery(adminUsersActions.TRIGGER_INIT_ADMIN_USERS, triggerInitAdminUsers)
	yield takeEvery(adminUsersActions.UPDATE_RESET_PASSWORD, updateResetPassword)
	yield takeEvery(adminUsersActions.EDIT_USER_PROFILE_IMAGE, editUserProfileImage)
	yield takeEvery(adminUsersActions.UPLDATE_PROFILE_IMAGE, updateProfileImage)
	yield takeEvery(adminUsersActions.UPDATE_USER, updateUser)
}

export default function* rootSaga() {
	yield all([fork(mainSaga)])
}
